import React, { useState, useEffect, useRef } from 'react'
import _ from 'lodash'
import moment from 'moment'
import Navbar from './Navbar'
import Pager from './Pager'
import StatusBtns from './StatusBtns'
import Reports from './Reports'
import MasterTable from './MasterTable'
import {
  watchClientLeads,
  watchClientLeadNotes,
  getOlderLeads,
  getOlderNotes,
  watchBillingClients,
  watchUserClient,
} from '../fire'
import { rowsPerPage, statusList } from '../constants'
import { justDigits, switchy } from '../utils'

let User = ({ setLoggedIn, clientId }) => {
  const [searchVal, setSearchVal] = useState('')
  const month = moment().format('YYYY-MM')
  const [page, setPage] = useState(0)
  const [sort, setSort] = useState({ field: 'date', order: 'desc' })
  const [{ userClientReady, billingClientsReady, clients }, setClientsState] =
    useState({
      userClientReady: false,
      billingClientsReady: false,
      clients: [],
    })
  const [leadClientIds, setLeadClientIds] = useState([])
  const clientsReady = userClientReady && billingClientsReady

  useEffect(() => {
    if (clientsReady) {
      setLeadClientIds(_.map(clients, 'id'))
    }
  }, [clientsReady, clients])
  const [currentLeads, setLeads] = useState([])
  const [olderLeads, setOlderLeads] = useState([])
  const leads = [...olderLeads, ...currentLeads]
  const [leadNotes, setNotes] = useState({})
  const [shownStatuses, setShownStatuses] = useState(statusList)
  const [view, setView] = useState('leads') // or 'reports'
  const [fromDate, setFromDate] = useState(moment().startOf('month').valueOf())
  const [toDate, setToDate] = useState(moment().endOf('day').valueOf())

  const searchDateRange = async ({
    currentMonth = moment().subtract(1, 'month').format('YYYY-MM'),
    oldLeads = [],
    oldNotes = [],
  } = {}) => {
    let fromMonth = moment(fromDate).format('YYYY-MM')
    if (currentMonth < fromMonth) {
      setOlderLeads(oldLeads)
      setNotes(prev => _.merge({}, prev, oldNotes))
      setPage(0)
      return
    }
    let leadOldies = await getOlderLeads({ month: currentMonth, leadClientIds })
    let noteOldies = await getOlderNotes({ month: currentMonth, leadClientIds })
    return searchDateRange({
      currentMonth: moment(currentMonth, 'YYYY-MM')
        .subtract(1, 'month')
        .format('YYYY-MM'),
      oldLeads: oldLeads.concat(leadOldies),
      oldNotes: _.merge({}, oldNotes, noteOldies),
    })
  }

  let searchDateRangeRef = useRef()
  searchDateRangeRef.current = searchDateRange // linty.  could maybe do useCallback instead but oh well
  useEffect(() => {
    searchDateRangeRef.current()
  }, [fromDate, toDate])

  useEffect(() => {
    if (!clientId) {
      // reload and get clientId since now logged in
      window.location.reload()
      return
    }

    let removeBillingClientListener = watchBillingClients(clientId, c => {
      setClientsState(prev => ({
        ...prev,
        billingClientsReady: true,
        clients: _.uniqBy([...c, ...prev.clients], 'id'),
      }))
    })
    let removeClientListener = watchUserClient(clientId, c => {
      setClientsState(prev => ({
        ...prev,
        userClientReady: true,
        clients: _.uniqBy([c, ...prev.clients], 'id'),
      }))
    })

    return () => {
      removeClientListener()
      removeBillingClientListener()
    }
  }, [clientId])

  useEffect(() => {
    if (_.isEmpty(leadClientIds)) return
    let removeLeadListeners = watchClientLeads({ month, leadClientIds }, l => {
      setLeads(prev => _.uniqBy([...l, ...prev], 'leadId'))
    })
    let removeNoteListeners = watchClientLeadNotes(
      { month, leadClientIds },
      moreNotes => {
        setNotes(prev => ({ ...prev, ...moreNotes }))
      }
    )

    return () =>
      _.forEach([...removeLeadListeners, ...removeNoteListeners], remove =>
        remove()
      )
  }, [month, leadClientIds])

  const filteredLeads = _(leads)
    .filter(l => {
      let digitOnly = justDigits(searchVal)
      let latestDate = l.sentDate || l.date // use sentDate if there, otherwise use date received
      return (
        latestDate >= fromDate &&
        latestDate <= toDate &&
        (_.includes(shownStatuses, l.status) ||
          shownStatuses.length === statusList.length) &&
        (_.includes(_.toLower(l.name), searchVal) ||
          _.includes(_.toLower(l.address), searchVal) ||
          _.includes(_.toLower(l.city), searchVal) ||
          _.includes(_.toLower(l.email), searchVal) ||
          _.includes(_.toLower(l.clientId), searchVal) ||
          _.includes(_.toLower(l.leadClientId), searchVal) ||
          (digitOnly && _.includes(justDigits(l.phoneNumber), digitOnly)))
      )
    })
    .orderBy(['date'], ['desc'])
    .value()

  return (
    <div className="container-fluid px-0">
      <Navbar
        {...{
          fromDate,
          setFromDate,
          toDate,
          setToDate,
          searchVal,
          setSearchVal,
          setPage,
          setLoggedIn,
          view,
          setView,
        }}
      />
      <div className="p-2">
        <div className="my-2">
          <StatusBtns
            leads={leads}
            shownStatuses={shownStatuses}
            setShownStatuses={setShownStatuses}
          />
        </div>
        {switchy(view, {
          leads: () => (
            <>
              <MasterTable
                {...{
                  clients,
                  page,
                  leads: filteredLeads,
                  setLeads,
                  leadNotes,
                  sort,
                  setSort,
                  month,
                  searchVal,
                }}
              />
              <Pager
                page={page}
                pageCount={Math.ceil(filteredLeads.length / rowsPerPage)}
                setPage={setPage}
              />
            </>
          ),
          reports: () => <Reports {...{ filteredLeads }} />,
        })}
      </div>
    </div>
  )
}

export default User
